import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useI18n } from "../context/I18nContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  ChatIcon,
  UsersIcon,
  ConnectionIcon,
  SettingsIcon,
  LogoutIcon,
  LogoIcon,
  TrashIcon,
  TemplateIcon,
  DashboardIcon,
  HistoryIcon,
  MetaIcon
} from "../components/Icons";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/dashboard", icon: DashboardIcon, label: t("nav.dashboard"), end: true },
    { path: "/dashboard/whatsapp", icon: ChatIcon, label: t("nav.whatsapp") },
    { path: "/dashboard/templates", icon: TemplateIcon, label: t("nav.templates") },
    { path: "/dashboard/history", icon: HistoryIcon, label: t("nav.history") },
    { path: "/dashboard/users", icon: UsersIcon, label: t("nav.users") },
    { path: "/dashboard/connections", icon: ConnectionIcon, label: t("nav.connections") },
    { path: "/dashboard/meta-connections", icon: MetaIcon, label: t("nav.meta") },
    { path: "/dashboard/inbox", icon: ChatIcon, label: t("nav.inbox") },
    { path: "/dashboard/publish", icon: TemplateIcon, label: t("nav.publish") },
    { path: "/dashboard/insights", icon: DashboardIcon, label: t("nav.insights") },
    { path: "/dashboard/settings", icon: SettingsIcon, label: t("nav.settings") },
    { path: "/dashboard/data-deletion", icon: TrashIcon, label: t("nav.dataDeletion"), danger: true }
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-[260px] bg-gray-900 flex flex-col">
        <div className="flex items-center gap-3 px-5 h-[72px] border-b border-gray-800">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <LogoIcon className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">{settings.companyName}</span>
        </div>

        <nav className="flex-1 px-3 py-6">
          <p className="px-3 mb-3 text-[10px] font-semibold text-gray-500 tracking-wider uppercase">
            {t("nav.menu")}
          </p>
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 h-11 rounded-lg mb-1 transition-all ${
                    isActive
                      ? item.danger
                        ? "bg-red-500 text-white shadow-lg shadow-red-500/20"
                        : "bg-whatsapp text-white shadow-lg shadow-whatsapp/20"
                      : item.danger
                        ? "text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`
                }
              >
                <IconComponent className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User info */}
        {user && (
          <div className="px-3 py-4 border-t border-gray-800">
            <div className="flex items-center gap-3 px-3 mb-3">
              <div className="w-8 h-8 bg-whatsapp/20 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-whatsapp">
                  {user.name?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user.name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          </div>
        )}

        <div className="p-3 border-t border-gray-800">
          <LanguageSwitcher className="mb-2" />
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 h-11 text-gray-400 hover:bg-red-500/10 hover:text-red-400 rounded-lg transition-all"
          >
            <LogoutIcon className="w-5 h-5" />
            <span className="text-sm font-medium">{t("nav.logout")}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default Dashboard;
